import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import BottomSheet from "./BottomSheet";
import { doneView, igConnectStage } from "./IgConnectSteps";
import { useAndroidBack } from "../hooks/useAndroidBack";
import InstagramGlyph from "./InstagramGlyph";
import VerifiedTick, { VerifiedTickTease } from "./VerifiedTick";
import { onComputer, openInstagramInApp, useInstagramDmVerify } from "../hooks/useInstagramDmVerify";
import { T } from "../i18n/T";
import { t } from "../i18n";
import "./IgConnectRow.css";

// My Profile's Instagram row once fans connect Instagram by DM (founder,
// round 7): a fan can only add Instagram by proving it, so there is no typing
// box — "Connect Instagram ›", then their handle with the green rosette and
// "Change". While we wait for their message: on a phone, Open Instagram; on a
// computer, a QR to scan with the phone (Instagram's website drops the
// one-time code from the link; the phone's Instagram app keeps it).
//
// On Edit Profile it is one row (founder, 2026-11-02): the handle with the green
// rosette and an arrow. A tap on it offers Change (connect another account) and
// Remove (1.5.3). Remove asks nothing first, like Remove on the venue and the
// trash on a link: the row simply goes back to Connect Instagram, which is also
// the way back.

/** "Instagram connected!" with the verified moment, over My Profile: every way
    of connecting gets it. Continue goes back to My Profile, handle and all. */
function ConnectedOverlay({ username, onDone }: { username: string; onDone: () => void }) {
  useAndroidBack(onDone);
  return createPortal(
    igConnectStage("done", doneView({ username, continueLabel: t("fanConnect.continue"), onContinue: onDone }), true),
    document.body
  );
}

export default function IgConnectRow({
  startNow,
  connectedAs,
  onConnected,
  onRemove,
}: {
  /** The panel's little Verify brought the fan here: start the message at once. */
  startNow?: boolean;
  /** The handle Instagram vouched for, or null when not connected. */
  connectedAs: string | null;
  onConnected: (username: string) => void;
  /** Take Instagram off the account. Rejects when the server didn't take it off. */
  onRemove: () => Promise<void>;
}) {
  const [justConnected, setJustConnected] = useState<string | null>(null);
  const v = useInstagramDmVerify((username) => {
    onConnected(username);
    setJustConnected(username);
  });
  const waiting = v.phase === "waiting";
  // The message screen right away; Instagram itself opens on the fan's own tap.
  useEffect(() => {
    if (startNow && !connectedAs) void v.start({ open: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per Verify tap
  }, [startNow]);
  const [manageOpen, setManageOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeFailed, setRemoveFailed] = useState(false);
  const start = () => {
    setRemoveFailed(false);
    setManageOpen(false);
    void v.start();
  };
  const remove = () => {
    setRemoving(true);
    setRemoveFailed(false);
    onRemove()
      .then(() => setManageOpen(false))
      .catch(() => setRemoveFailed(true))
      .finally(() => setRemoving(false));
  };

  let body: React.ReactNode;
  if (waiting && onComputer()) {
    body = (
      <div className="igc-panel" role="status">
        <div className="igc-panel-title">{t("fanConnect.scanTitle")}</div>
        <div className="igc-panel-sub">{t("fanConnect.scanSub")}</div>
        {v.qrSvg && (
          <div className="igc-qr">
            <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(v.qrSvg)}`} alt="" />
            <span className="igc-qr-logo">
              <InstagramGlyph size={32} />
            </span>
          </div>
        )}
        <div className="igc-wait">
          <span className="igc-spinner" aria-hidden="true" />
          <span>{t("fanConnect.waiting")}</span>
          <button type="button" className="igc-cancel" onClick={v.cancel}>
            {t("fanConnect.cancel")}
          </button>
        </div>
      </div>
    );
  } else if (waiting) {
    body = (
      <div className="igc-panel" role="status">
        <div className="igc-panel-title">{t("fanConnect.waitTitle")}</div>
        <div className="igc-panel-sub">
          <T k="fanConnect.anyMessage" vars={{ handle: v.handle }} tags={{ 0: (c) => <b>{c}</b> }} />
        </div>
        <a
          className="igc-open"
          href={v.url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            if (Capacitor.isNativePlatform() && v.url) {
              e.preventDefault();
              openInstagramInApp(v.url);
            }
          }}
        >
          {t("fanConnect.openInstagram")}
        </a>
        <div className="igc-wait">
          <span className="igc-spinner" aria-hidden="true" />
          <span>{t("fanConnect.waiting")}</span>
          <button type="button" className="igc-cancel" onClick={v.cancel}>
            {t("fanConnect.cancel")}
          </button>
        </div>
      </div>
    );
  } else {
    const chevron = (
      <svg className="fpe-row-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m9 5 7 7-7 7" />
      </svg>
    );
    body = connectedAs ? (
      <button type="button" className="fpe-row igc-row" onClick={() => setManageOpen(true)}>
        <span className="fpe-row-ic"><InstagramGlyph size={26} /></span>
        <span className="fpe-row-text">
          <span className="fpe-row-label">{t("fanProfileEditorSheet.instagram")}</span>
          <span className="fpe-row-value igc-handle">@{connectedAs}</span>
        </span>
        <VerifiedTick className="igc-tick" label={t("fanConnect.verified")} />
        {chevron}
      </button>
    ) : (
      <button type="button" className="fpe-row igc-connect" onClick={start} disabled={v.phase === "starting"}>
        <span className="fpe-row-ic"><InstagramGlyph size={26} /></span>
        <span className="fpe-row-text">
          <span className="fpe-row-label">{t("fanProfileEditorSheet.verifyWithInstagram")}<VerifiedTickTease /></span>
          <span className="fpe-row-value fpe-row-value--ask">{t("fanConnect.connectButton")}</span>
        </span>
        {chevron}
      </button>
    );
  }

  return (
    <>
      {body}
      {(v.phase === "expired" || v.phase === "taken") && <p className="igc-note">{t("fanProfileEditorSheet.tookTooLong")}</p>}
      {v.phase === "failed" && <p className="igc-note">{t("fanConnect.failed")}</p>}
      {manageOpen && connectedAs && (
        <BottomSheet open title={t("fanProfileEditorSheet.instagram")} onClose={() => setManageOpen(false)}>
          <div className="igc-manage-handle">
            <InstagramGlyph size={20} />
            <span>@{connectedAs}</span>
            <VerifiedTick className="igc-tick" label={t("fanConnect.verified")} />
          </div>
          <button type="button" className="igc-manage-btn igc-change" onClick={start} disabled={v.phase === "starting" || removing}>
            {t("fanProfileEditorSheet.change")}
          </button>
          <button
            type="button"
            className="igc-manage-btn igc-remove"
            onClick={remove}
            disabled={v.phase === "starting" || removing}
            aria-label={t("fanProfileEditorSheet.removeInstagram")}
          >
            {t("fanProfileEditorSheet.remove")}
          </button>
          {removeFailed && (
            <p className="igc-note" role="status">
              {t("fanProfileEditorSheet.couldntRemove")}
            </p>
          )}
        </BottomSheet>
      )}
      {justConnected && <ConnectedOverlay username={justConnected} onDone={() => setJustConnected(null)} />}
    </>
  );
}
